import { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Alert, Box, Chip,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { batchCreateUsers } from '../api/users';
import type { BatchCreateUserResponse } from '../api/types';

interface ParsedRow {
  index: number;
  synoUsername: string;
  displayName: string;
  email: string;
  department: string;
  isAdmin: boolean;
  error?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function BatchImportDialog({ open, onClose, onSuccess }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [step, setStep] = useState<'upload' | 'preview' | 'result'>('upload');
  const [result, setResult] = useState<BatchCreateUserResponse | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const parseCSV = (text: string): ParsedRow[] => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];

    // 检测表头
    const header = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/["']/g, ''));
    const idxMap: Record<string, number> = {};
    header.forEach((h, i) => { idxMap[h] = i; });

    // 列映射：支持多种表头格式
    const synoCol = idxMap['synousername'] ?? idxMap['username'] ?? idxMap['用户名'] ?? idxMap['账号'] ?? 0;
    const nameCol = idxMap['displayname'] ?? idxMap['name'] ?? idxMap['姓名'] ?? idxMap['显示名'] ?? -1;
    const emailCol = idxMap['email'] ?? idxMap['邮箱'] ?? idxMap['邮件'] ?? -1;
    const deptCol = idxMap['department'] ?? idxMap['部门'] ?? -1;
    const adminCol = idxMap['isadmin'] ?? idxMap['管理员'] ?? -1;

    const rows: ParsedRow[] = [];
    const seenNames = new Set<string>();
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',').map(c => c.trim().replace(/^["'](.*)["']$/, '$1'));
      const synoUsername = cols[synoCol]?.trim() || '';
      const displayName = nameCol >= 0 ? cols[nameCol]?.trim() || '' : '';
      const email = emailCol >= 0 ? cols[emailCol]?.trim() || '' : '';
      const department = deptCol >= 0 ? cols[deptCol]?.trim() || '' : '';
      const isAdmin = adminCol >= 0 ? /^(true|yes|是|1|管理员)$/i.test(cols[adminCol]?.trim() || '') : false;

      const row: ParsedRow = { index: i - 1, synoUsername, displayName, email, department, isAdmin };

      if (!synoUsername) {
        row.error = '用户名不能为空';
      } else if (seenNames.has(synoUsername)) {
        row.error = '重复的用户名';
      }
      if (synoUsername) seenNames.add(synoUsername);
      rows.push(row);
    }
    return rows;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCSV(text);
      setParsed(rows);
      setStep('preview');
      setError('');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    const valid = parsed.filter(r => !r.error);
    if (valid.length === 0) {
      setError('没有可导入的有效用户');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const res = await batchCreateUsers({
        users: valid.map(r => ({
          synoUsername: r.synoUsername,
          displayName: r.displayName || undefined,
          email: r.email || undefined,
          department: r.department || undefined,
          isAdmin: r.isAdmin || undefined,
        })),
      });
      setResult(res.data);
      setStep('result');
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || '导入失败');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setParsed([]);
    setFileName('');
    setStep('upload');
    setResult(null);
    setError('');
    onClose();
  };

  const validCount = parsed.filter(r => !r.error).length;
  const errorCount = parsed.filter(r => r.error).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>批量导入用户</DialogTitle>
      <DialogContent>
        {/* Step 1: 上传 */}
        {step === 'upload' && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              上传 CSV 文件，列顺序：synoUsername, displayName, email, department, isAdmin<br />
              也支持群晖格式（第一列为 username）。
            </Typography>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              size="large"
            >
              选择 CSV 文件
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              hidden
              onChange={handleFileChange}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              示例：用户名,显示名,邮箱,部门,管理员
            </Typography>
          </Box>
        )}

        {/* Step 2: 预览 */}
        {step === 'preview' && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2">
                文件：{fileName} | 共 {parsed.length} 行
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={`有效 ${validCount}`} size="small" color="success" variant="outlined" />
                {errorCount > 0 && <Chip label={`错误 ${errorCount}`} size="small" color="error" variant="outlined" />}
              </Box>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>用户名</TableCell>
                    <TableCell>显示名</TableCell>
                    <TableCell>邮箱</TableCell>
                    <TableCell>部门</TableCell>
                    <TableCell>管理员</TableCell>
                    <TableCell>状态</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.map(row => (
                    <TableRow key={row.index} sx={row.error ? { bgcolor: 'error.50' } : {}}>
                      <TableCell>{row.index + 1}</TableCell>
                      <TableCell>{row.synoUsername}</TableCell>
                      <TableCell>{row.displayName || '—'}</TableCell>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell>{row.department || '—'}</TableCell>
                      <TableCell>{row.isAdmin ? '是' : '否'}</TableCell>
                      <TableCell>
                        {row.error ? (
                          <Chip label={row.error} size="small" color="error" />
                        ) : (
                          <Chip label="有效" size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Step 3: 结果 */}
        {step === 'result' && result && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Alert severity={result.failCount === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              导入完成：成功 {result.successCount} 个，失败 {result.failCount} 个
            </Alert>
            {result.errors.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240, mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>行号</TableCell>
                      <TableCell>用户名</TableCell>
                      <TableCell>错误信息</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((err, i) => (
                      <TableRow key={i}>
                        <TableCell>{err.index + 1}</TableCell>
                        <TableCell>{err.synoUsername}</TableCell>
                        <TableCell sx={{ color: 'error.main' }}>{err.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'preview' ? (
          <>
            <Button onClick={() => setStep('upload')}>返回</Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing || validCount === 0}
            >
              {importing ? '导入中...' : `导入 ${validCount} 个用户`}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose}>{step === 'result' ? '完成' : '取消'}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
